import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Fade,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  ShoppingCart,
  CalendarToday,
  CompareArrows,
  Refresh,
  Download,
  ArrowUpward,
  ArrowDownward,
  Assessment,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { api } from '../services/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface FinanceOperation {
  time: string;
  income: number;
  expense: number;
  cash: number;
  transfer: number;
  total: number;
  // Campos opcionales para compatibilidad con versiones anteriores del backend
  type?: 'payment' | 'expense';
  sale_id?: number;
  installment_number?: number;
  description?: string;
  net?: number;
}

interface FinanceDaySummary {
  day: string;
  income: number;
  expense: number;
  cash: number;
  transfer: number;
  total: number;
  net?: number;
}

interface FinanceSummary {
  income: number;
  expense: number;
  cash: number;
  transfer: number;
  total: number;
  net?: number;
  expenses?: number;
  net_result?: number;
  cash_amount?: number;
  transfer_amount?: number;
  sales_count?: number;
  customers_count?: number;
  label?: string;
}

interface FinanceRange {
  start: string;
  end: string;
}

interface ComparisonData {
  current_period: FinanceSummary;
  previous_period: FinanceSummary;
  growth_percentage: number;
}

interface MonthlyBreakdown {
  month: string;
  collected: number;
  label: string;
}

interface CollectionMetrics {
  collected_week: number;
  collected_month: number;
  collected_year: number;
  total_pending: number;
  total_sales_value: number;
  total_collected_all: number;
  collection_rate: number;
  monthly_breakdown: MonthlyBreakdown[];
}

interface WeeklyProjection {
  week_start: string;
  week_end: string;
  expected_amount: number;
  installment_count: number;
}

interface ProjectionsData {
  total_cost: number;
  total_sales_value: number;
  total_collected: number;
  total_pending: number;
  gross_profit: number;
  weekly_projections: WeeklyProjection[];
}

interface SalesVendorBreakdown {
  name: string;
  count: number;
  total: number;
}

interface SalesPeriod {
  count: number;
  total_amount: number;
  total_cost: number;
  by_vendor: SalesVendorBreakdown[];
}

interface MonthComparison {
  month: string;
  label: string;
  count: number;
  total_amount: number;
  total_cost: number;
  is_current: boolean;
  change_pct?: number;
}

interface RecentSale {
  sale_id: number;
  customer_name: string;
  product_name: string;
  total_amount: number;
  vendor_name: string;
  date: string;
}

interface SalesOverviewData {
  week: SalesPeriod;
  month: SalesPeriod;
  monthly_comparison: MonthComparison[];
  recent_sales: RecentSale[];
}

const getStartOfWeek = (date: Date): Date => {
  const wed = new Date(date);
  wed.setHours(0, 0, 0, 0);
  const jsDay = wed.getDay();
  const offsetToWednesday = (jsDay + 4) % 7;
  wed.setDate(wed.getDate() - offsetToWednesday);
  return wed;
};

const formatDateLabel = (value: string): string => {
  const [datePart] = value.split('T');
  const [year, month, day] = datePart.split('-');
  if (year && month && day) {
    return `${day}/${month}/${year}`;
  }
  return value;
};

export default function FinanceDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return today;
  });
  const [selectedWeek, setSelectedWeek] = useState(() => getStartOfWeek(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [comparisonMonth, setComparisonMonth] = useState(new Date());
  
  const [dailyOperations, setDailyOperations] = useState<FinanceOperation[]>([]);
  const [dailySummary, setDailySummary] = useState<FinanceSummary | null>(null);

  const [weeklyDays, setWeeklyDays] = useState<FinanceDaySummary[]>([]);
  const [weeklySummary, setWeeklySummary] = useState<FinanceSummary | null>(null);
  const [weeklyRange, setWeeklyRange] = useState<FinanceRange | null>(null);

  const [monthlyDays, setMonthlyDays] = useState<FinanceDaySummary[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<FinanceSummary | null>(null);
  const [monthlyRange, setMonthlyRange] = useState<FinanceRange | null>(null);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [projectionsData, setProjectionsData] = useState<ProjectionsData | null>(null);
  const [metricsData, setMetricsData] = useState<CollectionMetrics | null>(null);
  const [salesOverview, setSalesOverview] = useState<SalesOverviewData | null>(null);

  const [loading, setLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const weekHelperText = React.useMemo(() => {
    const wednesday = getStartOfWeek(selectedWeek);
    const tuesday = new Date(wednesday);
    tuesday.setDate(wednesday.getDate() + 6);
    return `Semana: ${format(wednesday, 'dd/MM/yyyy')} - ${format(tuesday, 'dd/MM/yyyy')}`;
  }, [selectedWeek]);

  // Cargar métricas siempre al montar (resumen visible arriba de tabs)
  useEffect(() => {
    fetchMetricsData();
  }, []);

  useEffect(() => {
    if (activeTab === 0 || (activeTab >= 1 && activeTab <= 3) || activeTab === 5 || activeTab === 6 || activeTab === 7) {
      fetchData();
    }
  }, [activeTab, selectedDate, selectedWeek, selectedMonth]);

  const fetchSalesOverview = async () => {
    const response = await api.get('/finance/sales-overview');
    setSalesOverview(response.data);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 0: // Ventas
          await fetchSalesOverview();
          break;
        case 1: // Diario
          await fetchDailyData();
          break;
        case 2: // Semanal
          await fetchWeeklyData();
          break;
        case 3: // Mensual
          await fetchMonthlyData();
          break;
        case 4: // Comparativo
          await fetchComparisonData();
          break;
        case 5: // Proyecciones
          await fetchProjectionsData();
          break;
        case 6: // Métricas
          await fetchMetricsData();
          break;
        case 7: // Gráficos
          await fetchWeeklyData();
          await fetchMetricsData();
          break;
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyData = async () => {
    const response = await api.get('/finance/daily', {
      params: {
        date: format(selectedDate, 'yyyy-MM-dd')
      }
    });
    setDailyOperations(response.data.operations || []);
    setDailySummary(response.data.summary || null);
  };

  const fetchWeeklyData = async () => {
    const response = await api.get('/finance/weekly', {
      params: {
        week: format(selectedWeek, 'yyyy-MM-dd')
      }
    });
    setWeeklyDays(response.data.days || []);
    setWeeklySummary(response.data.summary || null);
    setWeeklyRange(response.data.range || null);
  };

  const fetchMonthlyData = async () => {
    const response = await api.get('/finance/monthly', {
      params: {
        month: format(selectedMonth, 'yyyy-MM')
      }
    });
    setMonthlyDays(response.data.days || []);
    setMonthlySummary(response.data.summary || null);
    setMonthlyRange(response.data.range || null);
  };

  const fetchProjectionsData = async () => {
    const response = await api.get('/finance/projections');
    setProjectionsData(response.data);
  };

  const fetchMetricsData = async () => {
    const response = await api.get('/finance/collection-metrics');
    setMetricsData(response.data);
  };

  const fetchComparisonData = async () => {
    const response = await api.get('/finance/comparison', {
      params: {
        current_month: format(selectedMonth, 'yyyy-MM'),
        comparison_month: format(comparisonMonth, 'yyyy-MM')
      }
    });
    setComparisonData(response.data);
  };

  const handleDailyDateChange = (date: Date | null) => {
    if (!date) return;
    const normalized = new Date(date);
    normalized.setHours(12, 0, 0, 0);
    setSelectedDate(normalized);
  };

  const handleWeekChange = (date: Date | null) => {
    if (!date) return;
    setSelectedWeek(getStartOfWeek(date));
  };

  const exportToCSV = () => {
    let csvContent = '';
    let filename = '';

    switch (activeTab) {
      case 1: {
        csvContent = [
          ['Hora', 'Descripción', 'Ingreso', 'Egreso', 'Efectivo', 'Transferencia', 'TOTAL'],
          ...dailyOperations.map(op => {
            const total = op.total ?? op.net ?? (op.income - op.expense);
            return [
              op.time,
              (op.description ?? '').replace(/,/g, ' '),
              op.income.toFixed(2),
              op.expense.toFixed(2),
              op.cash.toFixed(2),
              op.transfer.toFixed(2),
              total.toFixed(2),
            ];
          }),
          dailySummary
            ? [
                'TOTAL',
                '',
                dailySummary.income.toFixed(2),
                dailySummary.expense.toFixed(2),
                dailySummary.cash.toFixed(2),
                dailySummary.transfer.toFixed(2),
                (dailySummary.total ?? dailySummary.net ?? 0).toFixed(2),
              ]
            : []
        ].map(row => row.join(',')).join('\n');
        filename = `finanzas_diarias_${format(selectedDate, 'yyyy-MM-dd')}.csv`;
        break;
      }
      case 2: {
        csvContent = [
          ['Día', 'Ingreso', 'Egreso', 'Efectivo', 'Transferencia', 'TOTAL'],
          ...weeklyDays.map(item => {
            const total = item.total ?? item.net ?? (item.income - item.expense);
            return [
              item.day,
              item.income.toFixed(2),
              item.expense.toFixed(2),
              item.cash.toFixed(2),
              item.transfer.toFixed(2),
              total.toFixed(2),
            ];
          }),
          weeklySummary
            ? [
                'TOTAL',
                weeklySummary.income.toFixed(2),
                weeklySummary.expense.toFixed(2),
                weeklySummary.cash.toFixed(2),
                weeklySummary.transfer.toFixed(2),
                (weeklySummary.total ?? weeklySummary.net ?? 0).toFixed(2),
              ]
            : []
        ].map(row => row.join(',')).join('\n');
        filename = `finanzas_semanales_${format(selectedWeek, 'yyyy-MM-dd')}.csv`;
        break;
      }
      case 3: {
        csvContent = [
          ['Día', 'Ingreso', 'Egreso', 'Efectivo', 'Transferencia', 'TOTAL'],
          ...monthlyDays.map(item => {
            const total = item.total ?? item.net ?? (item.income - item.expense);
            return [
              item.day,
              item.income.toFixed(2),
              item.expense.toFixed(2),
              item.cash.toFixed(2),
              item.transfer.toFixed(2),
              total.toFixed(2),
            ];
          }),
          monthlySummary
            ? [
                'TOTAL',
                monthlySummary.income.toFixed(2),
                monthlySummary.expense.toFixed(2),
                monthlySummary.cash.toFixed(2),
                monthlySummary.transfer.toFixed(2),
                (monthlySummary.total ?? monthlySummary.net ?? 0).toFixed(2),
              ]
            : []
        ].map(row => row.join(',')).join('\n');
        filename = `finanzas_mensuales_${format(selectedMonth, 'yyyy-MM')}.csv`;
        break;
      }
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getTabContent = (): React.ReactElement => {
    switch (activeTab) {
      case 0:
        return renderSalesOverview();
      case 1:
    return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <DatePicker
                label="Seleccionar Día"
                value={selectedDate}
                onChange={handleDailyDateChange}
              />
          <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportToCSV}
                sx={{ borderColor: '#6366F1', color: '#6366F1' }}
              >
                Exportar CSV
              </Button>
            </Box>
            {renderDailyTable()}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <DatePicker
                label="Seleccionar Semana (solo miércoles)"
                value={selectedWeek}
                onChange={handleWeekChange}
                shouldDisableDate={(date) => date.getDay() !== 3}
                slotProps={{
                  textField: {
                    helperText: weekHelperText,
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportToCSV}
                sx={{ borderColor: '#6366F1', color: '#6366F1' }}
              >
                Exportar CSV
              </Button>
            </Box>
            {weeklyRange && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rango: {formatDateLabel(weeklyRange.start)} - {formatDateLabel(weeklyRange.end)}
              </Typography>
            )}
            {renderWeeklyTable()}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <DatePicker
                label="Seleccionar Mes"
                value={selectedMonth}
                onChange={(date) => date && setSelectedMonth(date)}
                views={['year', 'month']}
              />
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportToCSV}
                sx={{ borderColor: '#6366F1', color: '#6366F1' }}
              >
                Exportar CSV
              </Button>
            </Box>
            {monthlyRange && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rango: {formatDateLabel(monthlyRange.start)} - {formatDateLabel(monthlyRange.end)}
              </Typography>
            )}
            {renderMonthlyTable()}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <DatePicker
                label="Mes Inicial"
                value={selectedMonth}
                onChange={(date) => date && setSelectedMonth(date)}
                views={['year', 'month']}
              />
              <DatePicker
                label="Mes a Comparar"
                value={comparisonMonth}
                onChange={(date) => date && setComparisonMonth(date)}
                views={['year', 'month']}
              />
              <Button
                variant="contained"
                onClick={fetchComparisonData}
                startIcon={<CompareArrows />}
                sx={{
                  background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  },
                  minWidth: 150,
                  height: 56
                }}
              >
                Comparar
              </Button>
            </Box>
            {renderComparison()}
                      </Box>
        );
      case 5:
        return renderProjections();
      case 6:
        return renderMetrics();
      case 7:
        return renderChartsTab();
      default:
        return <Box>Selecciona una vista</Box>;
    }
  };

  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

  const renderSalesOverview = (): React.ReactElement => {
    if (!salesOverview) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">Cargando resumen de ventas...</Typography>
        </Box>
      );
    }

    const { week, month, monthly_comparison, recent_sales } = salesOverview;

    return (
      <Box>
        {/* Week + Month summary cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Última semana */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderTop: '3px solid #6366F1' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>
                  Última semana (7 días)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 800, color: '#6366F1' }}>
                      {week.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">ventas</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 800, color: '#10B981' }}>
                      {fmtCurrency(week.total_amount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">monto total</Typography>
                  </Box>
                </Box>
                {week.total_cost > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Costo: {fmtCurrency(week.total_cost)}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: week.total_amount - week.total_cost >= 0 ? '#10B981' : '#EF4444' }}>
                      Margen: {fmtCurrency(week.total_amount - week.total_cost)}
                    </Typography>
                  </Box>
                )}
                {week.by_vendor.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
                      Por vendedor
                    </Typography>
                    {week.by_vendor.map((v) => (
                      <Box key={v.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2">{v.name} ({v.count})</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtCurrency(v.total)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Mes actual */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderTop: '3px solid #3B82F6' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>
                  Mes actual
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 800, color: '#3B82F6' }}>
                      {month.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">ventas</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 800, color: '#10B981' }}>
                      {fmtCurrency(month.total_amount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">monto total</Typography>
                  </Box>
                </Box>
                {month.total_cost > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Costo: {fmtCurrency(month.total_cost)}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: month.total_amount - month.total_cost >= 0 ? '#10B981' : '#EF4444' }}>
                      Margen: {fmtCurrency(month.total_amount - month.total_cost)}
                    </Typography>
                  </Box>
                )}
                {month.by_vendor.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
                      Por vendedor
                    </Typography>
                    {month.by_vendor.map((v) => (
                      <Box key={v.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2">{v.name} ({v.count})</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtCurrency(v.total)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Comparativo mensual */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Comparativo mensual (últimos 6 meses)
        </Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Mes</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ventas</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Monto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Costo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Margen</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Variación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthly_comparison.map((mc) => {
                const margin = mc.total_amount - mc.total_cost;
                return (
                  <TableRow
                    key={mc.month}
                    sx={mc.is_current ? { backgroundColor: 'rgba(99,102,241,0.08)' } : {}}
                  >
                    <TableCell sx={{ fontWeight: mc.is_current ? 700 : 400 }}>
                      {mc.label}
                      {mc.is_current && (
                        <Chip label="Actual" size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(99,102,241,0.15)', color: '#6366F1' }} />
                      )}
                    </TableCell>
                    <TableCell align="right">{mc.count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(mc.total_amount)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtCurrency(mc.total_cost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: margin >= 0 ? '#10B981' : '#EF4444' }}>
                      {fmtCurrency(margin)}
                    </TableCell>
                    <TableCell align="right">
                      {mc.change_pct !== undefined ? (
                        <Chip
                          size="small"
                          icon={mc.change_pct >= 0 ? <ArrowUpward sx={{ fontSize: '0.75rem !important' }} /> : <ArrowDownward sx={{ fontSize: '0.75rem !important' }} />}
                          label={`${mc.change_pct >= 0 ? '+' : ''}${mc.change_pct}%`}
                          color={mc.change_pct >= 0 ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Últimas ventas */}
        {recent_sales.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
              Últimas ventas (esta semana)
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendedor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent_sales.map((s) => (
                    <TableRow key={s.sale_id}>
                      <TableCell>{formatDateLabel(s.date)}</TableCell>
                      <TableCell>{s.customer_name}</TableCell>
                      <TableCell>{s.product_name}</TableCell>
                      <TableCell>
                        <Chip label={s.vendor_name} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(s.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    );
  };

  const renderProjections = (): React.ReactElement => {
    if (!projectionsData) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">Cargando proyecciones...</Typography>
        </Box>
      );
    }

    const today = new Date();
    const currentWeekStart = getStartOfWeek(today);
    const currentWeekISO = currentWeekStart.toISOString().split('T')[0];

    return (
      <Box>
        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Costo Mercadería', value: projectionsData.total_cost, color: '#EF4444' },
            { label: 'Valor Ventas', value: projectionsData.total_sales_value, color: '#6366F1' },
            { label: 'Cobrado', value: projectionsData.total_collected, color: '#10B981' },
            { label: 'Pendiente', value: projectionsData.total_pending, color: '#F59E0B' },
            { label: 'Ganancia Bruta', value: projectionsData.gross_profit, color: projectionsData.gross_profit >= 0 ? '#10B981' : '#EF4444' },
          ].map((card) => (
            <Grid item xs={6} sm={4} md={2.4} key={card.label}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: card.color }}>
                    {fmtCurrency(card.value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Weekly projections table */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Proyección por semana (mié-mar)
        </Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Semana</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Monto esperado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cuotas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projectionsData.weekly_projections.map((wp) => {
                const isCurrentWeek = wp.week_start === currentWeekISO;
                return (
                  <TableRow
                    key={wp.week_start}
                    sx={isCurrentWeek ? { backgroundColor: 'rgba(99,102,241,0.08)' } : {}}
                  >
                    <TableCell sx={{ fontWeight: isCurrentWeek ? 700 : 400 }}>
                      {formatDateLabel(wp.week_start)} - {formatDateLabel(wp.week_end)}
                      {isCurrentWeek && (
                        <Chip label="Actual" size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(99,102,241,0.15)', color: '#6366F1' }} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmtCurrency(wp.expected_amount)}
                    </TableCell>
                    <TableCell align="right">{wp.installment_count}</TableCell>
                  </TableRow>
                );
              })}
              {projectionsData.weekly_projections.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="textSecondary">No hay proyecciones pendientes</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderMetrics = (): React.ReactElement => {
    if (!metricsData) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">Cargando métricas...</Typography>
        </Box>
      );
    }

    const rateColor = metricsData.collection_rate >= 70 ? '#10B981' : metricsData.collection_rate >= 40 ? '#F59E0B' : '#EF4444';

    return (
      <Box>
        {/* Row 1: Collected by period */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Cobrado Semana', value: metricsData.collected_week, color: '#10B981' },
            { label: 'Cobrado Mes', value: metricsData.collected_month, color: '#3B82F6' },
            { label: 'Cobrado Año', value: metricsData.collected_year, color: '#8B5CF6' },
          ].map((card) => (
            <Grid item xs={12} sm={4} key={card.label}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 800, color: card.color }}>
                    {fmtCurrency(card.value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Row 2: Pending + Rate */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Falta Cobrar
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 800, color: '#F59E0B' }}>
                  {fmtCurrency(metricsData.total_pending)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                  de {fmtCurrency(metricsData.total_sales_value)} en ventas activas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                  % Cobrado
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 800, color: rateColor }}>
                  {metricsData.collection_rate}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(metricsData.collection_rate, 100)}
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: rateColor,
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Monthly breakdown table */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Desglose mensual (últimos 6 meses)
        </Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Mes</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cobrado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metricsData.monthly_breakdown.map((mb) => {
                const isCurrentMonth = mb.month === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                return (
                  <TableRow
                    key={mb.month}
                    sx={isCurrentMonth ? { backgroundColor: 'rgba(99,102,241,0.08)' } : {}}
                  >
                    <TableCell sx={{ fontWeight: isCurrentMonth ? 700 : 400 }}>
                      {mb.label}
                      {isCurrentMonth && (
                        <Chip label="Actual" size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(99,102,241,0.15)', color: '#6366F1' }} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmtCurrency(mb.collected)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderDailyTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Hora</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Ingreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Egreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Efectivo</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Transferencia</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>TOTAL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dailyOperations.map((op, index) => {
            const total = op.total ?? op.net ?? (op.income - op.expense);
            return (
              <TableRow key={`${op.time}-${index}`}>
                <TableCell>{op.time}</TableCell>
                <TableCell>{op.description ?? '—'}</TableCell>
                <TableCell align="right">${op.income.toLocaleString()}</TableCell>
                <TableCell align="right">${op.expense.toLocaleString()}</TableCell>
                <TableCell align="right">${op.cash.toLocaleString()}</TableCell>
                <TableCell align="right">${op.transfer.toLocaleString()}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: total >= 0 ? 'success.main' : 'error.main' }}
                >
                  ${total.toLocaleString()}
                </TableCell>
              </TableRow>
            );
          })}
          {dailySummary && (
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${dailySummary.income.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${dailySummary.expense.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${dailySummary.cash.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${dailySummary.transfer.toLocaleString()}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: (dailySummary.total ?? dailySummary.net ?? 0) >= 0 ? 'success.main' : 'error.main'
                }}
              >
                ${(dailySummary.total ?? dailySummary.net ?? 0).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderWeeklyTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Día</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Ingreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Egreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Efectivo</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Transferencia</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>TOTAL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {weeklyDays.map((item) => {
            const total = item.total ?? item.net ?? (item.income - item.expense);
            return (
              <TableRow key={item.day}>
                <TableCell>{formatDateLabel(item.day)}</TableCell>
                <TableCell align="right">${item.income.toLocaleString()}</TableCell>
                <TableCell align="right">${item.expense.toLocaleString()}</TableCell>
                <TableCell align="right">${item.cash.toLocaleString()}</TableCell>
                <TableCell align="right">${item.transfer.toLocaleString()}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: total >= 0 ? 'success.main' : 'error.main' }}
                >
                  ${total.toLocaleString()}
                </TableCell>
              </TableRow>
            );
          })}
          {weeklySummary && (
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${weeklySummary.income.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${weeklySummary.expense.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${weeklySummary.cash.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${weeklySummary.transfer.toLocaleString()}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: (weeklySummary.total ?? weeklySummary.net ?? 0) >= 0 ? 'success.main' : 'error.main'
                }}
              >
                ${(weeklySummary.total ?? weeklySummary.net ?? 0).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMonthlyTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Día</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Ingreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Egreso</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Efectivo</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Transferencia</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>TOTAL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {monthlyDays.map((item) => {
            const total = item.total ?? item.net ?? (item.income - item.expense);
            return (
              <TableRow key={item.day}>
                <TableCell>{formatDateLabel(item.day)}</TableCell>
                <TableCell align="right">${item.income.toLocaleString()}</TableCell>
                <TableCell align="right">${item.expense.toLocaleString()}</TableCell>
                <TableCell align="right">${item.cash.toLocaleString()}</TableCell>
                <TableCell align="right">${item.transfer.toLocaleString()}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: total >= 0 ? 'success.main' : 'error.main' }}
                >
                  ${total.toLocaleString()}
                </TableCell>
              </TableRow>
            );
          })}
          {monthlySummary && (
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${monthlySummary.income.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${monthlySummary.expense.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${monthlySummary.cash.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                ${monthlySummary.transfer.toLocaleString()}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  color: (monthlySummary.total ?? monthlySummary.net ?? 0) >= 0 ? 'success.main' : 'error.main'
                }}
              >
                ${(monthlySummary.total ?? monthlySummary.net ?? 0).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderComparison = () => {
    if (!comparisonData) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            Selecciona los meses y haz clic en "Comparar"
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Elige el mes inicial y el mes a comparar, luego presiona el botón para ver el análisis comparativo.
          </Typography>
        </Box>
      );
    }

    const currentIncome = comparisonData.current_period.income ?? 0;
    const currentExpense = comparisonData.current_period.expenses ?? comparisonData.current_period.expense ?? 0;
    const currentNet = comparisonData.current_period.total ?? comparisonData.current_period.net ?? comparisonData.current_period.net_result ?? 0;
    const currentSales = comparisonData.current_period.sales_count ?? 0;
    const currentCustomers = comparisonData.current_period.customers_count ?? 0;

    const previousIncome = comparisonData.previous_period.income ?? 0;
    const previousExpense = comparisonData.previous_period.expenses ?? comparisonData.previous_period.expense ?? 0;
    const previousNet = comparisonData.previous_period.total ?? comparisonData.previous_period.net ?? comparisonData.previous_period.net_result ?? 0;
    const previousSales = comparisonData.previous_period.sales_count ?? 0;
    const previousCustomers = comparisonData.previous_period.customers_count ?? 0;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Período Actual
              </Typography>
                      <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                  ${currentIncome.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                            Ingresos
                          </Typography>
                        </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 700 }}>
                  ${currentExpense.toLocaleString()}
                        </Typography>
                <Typography variant="body2" color="textSecondary">
                  Egresos
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ 
                  color: currentNet >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 700 
                }}>
                  ${currentNet.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Resultado Neto
                          </Typography>
                        </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentSales}
                        </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Ventas
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentCustomers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Clientes
                  </Typography>
                </Box>
              </Box>
                </CardContent>
              </Card>
            </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Período Anterior
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700 }}>
                  ${previousIncome.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ingresos
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 700 }}>
                  ${previousExpense.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Egresos
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ 
                  color: previousNet >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 700 
                }}>
                  ${previousNet.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Resultado Neto
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {previousSales}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Ventas
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {previousCustomers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Clientes
                  </Typography>
                </Box>
              </Box>
                </CardContent>
              </Card>
            </Grid>

        <Grid item xs={12}>
          <Card sx={{ background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)' }}>
                <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                Comparativo de Crecimiento
                  </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={comparisonData.growth_percentage >= 0 ? <ArrowUpward /> : <ArrowDownward />}
                  label={`${comparisonData.growth_percentage >= 0 ? '+' : ''}${comparisonData.growth_percentage.toFixed(1)}%`}
                  color={comparisonData.growth_percentage >= 0 ? 'success' : 'error'}
                  sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                />
                <Typography variant="body1" color="textSecondary">
                  Crecimiento en Resultado Neto
                      </Typography>
                    </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
    );
  };

  const renderChartsTab = () => {
    const weekChartData = weeklyDays.map((d) => ({
      name: new Date(d.day + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit' }),
      Ingresos: d.income,
      Gastos: d.expense,
      Neto: d.total ?? d.net ?? (d.income - d.expense),
    }));

    const monthChartData = metricsData?.monthly_breakdown?.map((mb) => ({
      name: mb.label,
      Cobrado: mb.collected,
    })) || [];

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Ingresos vs Gastos - Semana actual
        </Typography>
        <Paper sx={{ p: 2, mb: 4 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weekChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Resultado Neto Diario - Semana actual
        </Typography>
        <Paper sx={{ p: 2, mb: 4 }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weekChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
              <Legend />
              <Line type="monotone" dataKey="Neto" stroke="#6366F1" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          Evolución de Cobranzas Mensuales
        </Typography>
        <Paper sx={{ p: 2 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="Cobrado" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography 
            variant="h4" 
            sx={(theme) => ({
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              fontWeight: 700,
              ...(theme.palette.mode === 'dark'
                ? { color: theme.palette.common.white }
                : {
                    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }),
            })}
          >
            Dashboard Financiero
                                </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Actualizar datos">
                                <IconButton
                onClick={fetchData}
            sx={{ 
                  background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Refresh />
                                </IconButton>
                              </Tooltip>
                            </Box>
          </Box>

        {/* Resumen general - siempre visible */}
        {metricsData && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Cobrado Semana', value: metricsData.collected_week, color: '#10B981' },
              { label: 'Cobrado Mes', value: metricsData.collected_month, color: '#3B82F6' },
              { label: 'Cobrado Año', value: metricsData.collected_year, color: '#8B5CF6' },
              { label: 'Falta Cobrar', value: metricsData.total_pending, color: '#EF4444' },
            ].map((card) => (
              <Grid item xs={6} sm={3} key={card.label}>
                <Card sx={(t) => ({
                  background: t.palette.mode === 'dark' ? 'rgba(22,27,46,0.6)' : '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderTop: `3px solid ${card.color}`,
                })}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: card.color }}>
                      {fmtCurrency(card.value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 2, mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: { xs: 44, sm: 48 },
                fontWeight: 500,
                textTransform: 'none',
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                height: 3,
                borderRadius: '2px 2px 0 0',
              }
            }}
          >
            <Tab
              icon={<ShoppingCart />}
              label="Ventas"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarToday />}
              label="Vista Diaria"
              iconPosition="start"
            />
            <Tab
              icon={<Assessment />}
              label="Vista Semanal"
              iconPosition="start"
            />
            <Tab
              icon={<TrendingUp />}
              label="Vista Mensual"
              iconPosition="start"
            />
            <Tab
              icon={<CompareArrows />}
              label="Comparativo"
              iconPosition="start"
            />
            <Tab
              icon={<TrendingUp />}
              label="Proyecciones"
              iconPosition="start"
            />
            <Tab
              icon={<Assessment />}
              label="Métricas"
              iconPosition="start"
            />
            <Tab
              icon={<TrendingUp />}
              label="Gráficos"
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Content */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress />
            </Box>
          ) : (
            <Fade in timeout={300}>
              {getTabContent()}
            </Fade>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
}
